import { AVAILABLE_LOCALES, LOCALES } from 'localization/constants/configEnums';
import { t } from 'localization/utils/t';
import { startCase } from './startCase';

const TP = 'shared.utils.pageMetadataUtils';
/*
construct list creates a list to be joined in text for the pageTitle or pageDescription
It takes a list of objects, the key that needs to be used to pull out the value, and an option
to change the value from 'sneakers` to `shoes`

list = [
    {
        "group_id": "sneakers",
        "display_name": "Sneakers",
    }
    {
        "group_id": "apparel",
        "display_name": "Apparel",
    }
    {
        "group_id": "accessories",
        "display_name": "Accessories",
    }
]

key = 'group_id'

descriptionList = ['shoes', ', ', 'apparel', ' & ', 'accessories']

when joined the string should look like => 'shoes, apparel & accessories'
*/

export const constructList = ({ list = [], key, sneakersToShoes = true }) => {
  const constructedList = [];

  for (const [index, obj] of list.entries()) {
    if (index === list?.length - 1 && list?.length >= 2) {
      constructedList.push(' & ');
    }
    const listValue =
      sneakersToShoes && obj[key] === 'sneakers' ? t(`${TP}.shoes`, 'shoes') : obj[key];
    const formattedListValue = listValue.replace(/_+/g, ' ');
    constructedList.push(formattedListValue);

    if (index < list?.length - 2 && list?.length > 2) {
      constructedList.push(', ');
    }
  }
  const constructedListWithStartCase = constructedList?.map((value) => startCase(value));

  // allowing for two different lists, one default and one with start case
  // constructedListWithStartCase usually used for titles and default contructedList used for descriptions
  return { constructedList, constructedListWithStartCase };
};

export const sortAndSliceList = (list, lengthToSlice = 3) => {
  const sortedList = list?.sort((a, b) => b?.count - a?.count) || [];
  const listLength = sortedList?.length || 0;

  return listLength > 2 ? sortedList?.slice(0, lengthToSlice) : sortedList;
};

export const getCategories = (categoryGroups) => {
  const subCategoryGroups = [];
  const groups =
    categoryGroups?.[0]?.group_id === 'all' ? categoryGroups?.[0]?.children : categoryGroups;

  const getTopLevelCategoriesChildren = (group) => {
    if (group.group_id === 'sneakers') {
      subCategoryGroups.push(group);
    } else if (group?.children?.[0]) {
      subCategoryGroups.push(group?.children?.[0]);
    }
  };

  const getTopLevelCategoryChildren = (group) => {
    if (group.group_id === 'sneakers') {
      subCategoryGroups.push(group);
    }
    if (group?.children) {
      const children = group?.children;
      for (const categoryGroup of children) {
        subCategoryGroups.push(categoryGroup);
      }
    }
  };

  for (const group of groups) {
    if (categoryGroups?.[0]?.group_id === 'all') {
      getTopLevelCategoriesChildren(group);
    } else {
      getTopLevelCategoryChildren(group);
    }
  }

  const topLevelCategories = constructList({ list: sortAndSliceList(groups), key: 'group_id' });
  const subCategories = constructList({
    list: sortAndSliceList(subCategoryGroups),
    key: 'group_id',
    sneakersToShoes: false,
  });

  return {
    topLevelCategories,
    subCategories,
  };
};

export const getFacet = (facets, name) => {
  return facets?.find((facet) => facet.name === name);
};

export const getText = (text: string, textToAppend: string | string[]) => {
  if (Array.isArray(textToAppend) && textToAppend?.length > 0) {
    if (!text || text.length === 0) {
      return textToAppend?.join('');
    }
    return `${text} ${textToAppend?.join('')}`;
  }

  if (typeof textToAppend === 'string' && textToAppend?.length > 0) {
    if (!text || text.length === 0) {
      return textToAppend;
    }
    return `${text} ${textToAppend}`;
  }

  return '';
};

export const appendGOATandCountryCodeToTitle = (title: string, locale: string = LOCALES.enUS) => {
  if (!title) return '';

  let titleOfPage = title;

  if (!titleOfPage.endsWith('GOAT')) {
    titleOfPage = t(`${TP}.appendGOATandCountryCodeToTitle`, '{titleOfPage} | GOAT', {
      titleOfPage,
    });
  }

  if (locale !== LOCALES.enUS && AVAILABLE_LOCALES[locale]) {
    titleOfPage = `${titleOfPage} ${String(AVAILABLE_LOCALES[locale].country)}`;
  }
  return titleOfPage;
};

export const stripSpecialCharacterFromString = (string: string) => {
  return string?.replace(/[_-]+/g, ' ');
};

export const getParentCategoryBasePath = (pathname) => {
  const topLevelCategories = new Set(['sneakers', 'apparel', 'accessories', 'bags', 'jewelry']);

  const nameOfPath = pathname?.split('/');
  const isCategoryBasePath =
    nameOfPath?.length &&
    nameOfPath[nameOfPath.length - 1] &&
    topLevelCategories.has(nameOfPath[nameOfPath.length - 1]);
  const parentCategory = nameOfPath[nameOfPath.length - 1];

  return { isCategoryBasePath, parentCategory };
};
